import type { LessonAiDraft, TeacherLessonRecord } from "../auth/api-client";
import type { ConsoleLine } from "../python-runtime/usePythonRuntime";
import type { DemoTourStep } from "../student-ide/demoTourSteps";

/**
 * Curated, hard-coded data that feeds the public landing-page showcases.
 * It is rendered through the REAL product components (lesson cards, charts,
 * progress panels, read-only IDE), so the marketing site stays visually in
 * sync as those components evolve. None of this hits the API or auth.
 */

const NOW_ISO = "2026-01-01T00:00:00.000Z";

export type DemoLinkedStandard = {
  readonly code: string;
  readonly description: string;
};

export type DemoLibraryLesson = TeacherLessonRecord & {
  linkedStandards?: readonly DemoLinkedStandard[];
};

function demoLesson(
  partial: Pick<TeacherLessonRecord, "id" | "title"> &
    Partial<TeacherLessonRecord> & { linkedStandards?: readonly DemoLinkedStandard[] }
): DemoLibraryLesson {
  return {
    slug: null,
    starterFilesJson: {},
    createdAt: NOW_ISO,
    updatedAt: NOW_ISO,
    workspaceMode: "simple",
    ...partial,
  };
}

/** A small, photogenic lesson library for the showcase grid. */
export const DEMO_LIBRARY_LESSONS: readonly DemoLibraryLesson[] = [
  demoLesson({
    id: "demo-loops",
    title: "Loops & a Guessing Game",
    description: "Use while-loops and input to build a number guessing game.",
    iconName: "dice",
    iconBgColor: "#1b4d2e",
    iconFgColor: "#fdf9f3",
    standardsCount: 2,
    linkedStandards: [
      { code: "2-AP-10", description: "Use flow control to make programs repeat steps." },
      { code: "3A-AP-23", description: "Create programs that include iteration." },
    ],
  }),
  demoLesson({
    id: "demo-turtle",
    title: "Draw with Turtle",
    description: "Loops + angles to draw colorful spirals on screen.",
    iconName: "palette",
    iconBgColor: "#7a3b8f",
    iconFgColor: "#fdf9f3",
  }),
  demoLesson({
    id: "demo-lists",
    title: "Lists & Playlists",
    description: "Store, sort, and shuffle a playlist of songs.",
    iconName: "music",
    iconBgColor: "#2a4d8f",
    iconFgColor: "#fdf9f3",
    standardsCount: 1,
    linkedStandards: [
      { code: "2-AP-11", description: "Represent the way programs store and manipulate data." },
    ],
  }),
  demoLesson({
    id: "demo-functions",
    title: "Functions: Build a Calculator",
    description: "Break a problem into reusable functions.",
    iconName: "calculator",
    iconBgColor: "#8f4d2a",
    iconFgColor: "#fdf9f3",
    standardsCount: 2,
    linkedStandards: [
      { code: "3B-AP-13", description: "Decompose problems into smaller tasks through procedures." },
      { code: "3B-AP-16", description: "Create procedures with parameters to generalize solutions." },
    ],
  }),
  demoLesson({
    id: "demo-text-adventure",
    title: "Text Adventure Engine",
    description: "A multi-file project with rooms, items, and a game loop.",
    iconName: "dragon",
    iconBgColor: "#2a8f7a",
    iconFgColor: "#fdf9f3",
    workspaceMode: "advanced",
  }),
  demoLesson({
    id: "demo-data-detective",
    title: "Data Detective",
    description: "Read a dataset, count things, and chart the results.",
    iconName: "chart-column",
    iconBgColor: "#8f2a4d",
    iconFgColor: "#fdf9f3",
    workspaceMode: "advanced",
  }),
  demoLesson({
    id: "demo-variables",
    title: "Variables & Output",
    description: "Print messages and store values students will use all year.",
    iconName: "terminal",
    iconBgColor: "#2a4d8f",
    iconFgColor: "#fdf9f3",
    standardsCount: 1,
    linkedStandards: [
      { code: "1B-AP-09", description: "Use variables to store and retrieve values in a program." },
    ],
  }),
  demoLesson({
    id: "demo-classes",
    title: "Intro to Classes",
    description: "Model real-world things as objects with attributes and methods.",
    iconName: "cubes",
    iconBgColor: "#5c3d8f",
    iconFgColor: "#fdf9f3",
    standardsCount: 1,
    linkedStandards: [
      { code: "3B-AP-14", description: "Create programs that use objects to combine data and procedures." },
    ],
  }),
  demoLesson({
    id: "demo-rps",
    title: "Rock, Paper, Scissors",
    description:
      "A fast intro to conditionals and randomness. Published so other teachers can copy it into their library.",
    iconName: "gamepad",
    iconBgColor: "#1b6b4d",
    iconFgColor: "#fdf9f3",
    communityPublishStatus: "approved",
  }),
  demoLesson({
    id: "demo-times-table-draft",
    title: "Times Table Builder",
    description:
      "AI-drafted and still in progress. Add it to a cohort plan or save it to your library when you are ready.",
    iconName: "calculator",
    iconBgColor: "#1b4d2e",
    iconFgColor: "#fdf9f3",
    isDraft: true,
    standardsCount: 1,
    linkedStandards: [
      { code: "3A-AP-23", description: "Create programs that include iteration." },
    ],
  }),
];

/** The prompt a teacher "types" before the AI animation runs. */
export const DEMO_AI_PROMPT =
  "A beginner lesson on for-loops where students print a multiplication table for a number they choose.";

/** The finished draft revealed when the AI animation completes. */
export const DEMO_AI_DRAFT: LessonAiDraft = {
  title: "Times Table Builder",
  description: "Students use a for-loop and input() to print a multiplication table.",
  iconName: "calculator",
  iconBgColor: "#1b4d2e",
  iconFgColor: "#fdf9f3",
  workspaceMode: "simple",
  mainPy: `# Times Table Builder
# Ask for a number, then print its multiplication table.

number = int(input("Pick a number: "))

for i in range(1, 11):
    # TODO: print "number x i = result" for each row
    print(number, "x", i, "=", number * i)
`,
  tourSteps: [
    {
      title: "What you'll build",
      body: [
        "You'll make a program that prints a full multiplication table.",
        "The user picks a number, and your for-loop does the rest.",
      ],
    },
    {
      title: "Loop from 1 to 10",
      body: [
        "Use `range(1, 11)` so your loop runs ten times.",
        "Each time through, multiply the chosen number by the counter.",
      ],
      referenceCode: "for i in range(1, 11):\n    print(i)",
    },
    {
      title: "Print each row",
      body: ["Show the math like `7 x 3 = 21` so the table is easy to read."],
    },
  ],
  tests: [
    {
      name: "Prints ten rows",
      description: "The table should have one line for each multiplier 1–10.",
      testCode: "assert output.count('x') >= 10",
      isRequired: true,
      visibility: "summary",
    },
    {
      name: "Uses the chosen number",
      description: "The output reflects the number the user entered.",
      testCode: "assert '= ' in output",
      isRequired: true,
      visibility: "summary",
    },
  ],
};

/** Second scene: evolving a specific cohort's lesson plan based on its needs. */
export const DEMO_AI_PROMPT_2 =
  "This cohort is having trouble with how classes work. Create a second lesson in the theme of Moby Dick.";

export const DEMO_AI_DRAFT_2: LessonAiDraft = {
  title: "Call Me Class-mael: Whales & Objects",
  description: "Reinforce how classes work by modeling whales from Moby Dick as Python objects.",
  iconName: "fish",
  iconBgColor: "#2a8f7a",
  iconFgColor: "#fdf9f3",
  workspaceMode: "simple",
  mainPy: `# Call Me Class-mael
# Model a whale from Moby Dick as an object so classes finally click.

class Whale:
    def __init__(self, name, color):
        self.name = name
        self.color = color

    def spout(self):
        print(f"{self.name} the {self.color} whale spouts a fountain!")

moby = Whale("Moby Dick", "white")
moby.spout()
`,
  tourSteps: [
    {
      title: "Why classes?",
      body: [
        "A class is a blueprint. Each whale you create from it is an object with its own data.",
        "We'll build a Whale and ask it to do something.",
      ],
    },
    {
      title: "Give the whale a method",
      body: ["`spout()` is a method, an action the whale object can perform."],
      referenceCode: "def spout(self):\n    print(self.name)",
    },
  ],
  tests: [
    {
      name: "Defines a Whale class",
      description: "There is a class named Whale.",
      testCode: "assert 'class Whale' in source",
      isRequired: true,
      visibility: "summary",
    },
    {
      name: "Calls a method",
      description: "An object is created and spout() is called.",
      testCode: "assert 'spout' in output",
      isRequired: true,
      visibility: "summary",
    },
  ],
};

/** A single row in the cohort's lesson plan view. */
export interface DemoPlanItem {
  readonly id: string;
  readonly title: string;
  readonly iconName: string;
  readonly bgColor: string;
  readonly fgColor: string;
}

/** Existing cohort plan; the new lesson is inserted right after "Intro to Classes". */
export const DEMO_COHORT_PLAN: readonly DemoPlanItem[] = [
  { id: "p1", title: "Variables & Output", iconName: "terminal", bgColor: "#2a4d8f", fgColor: "#fdf9f3" },
  { id: "p2", title: "Loops & a Guessing Game", iconName: "dice", bgColor: "#1b4d2e", fgColor: "#fdf9f3" },
  { id: "p3", title: "Intro to Classes", iconName: "cubes", bgColor: "#7a3b8f", fgColor: "#fdf9f3" },
  { id: "p4", title: "Lists & Playlists", iconName: "music", bgColor: "#8f4d2a", fgColor: "#fdf9f3" },
];

/** Insert the new lesson after this index (the "1st class exercise"). */
export const DEMO_PLAN_INSERT_AFTER = 2;

export const DEMO_PLAN_INSERTED: DemoPlanItem = {
  id: "p-new",
  title: DEMO_AI_DRAFT_2.title,
  iconName: DEMO_AI_DRAFT_2.iconName ?? "fish",
  bgColor: DEMO_AI_DRAFT_2.iconBgColor ?? "#2a8f7a",
  fgColor: DEMO_AI_DRAFT_2.iconFgColor ?? "#fdf9f3",
};

/** Third scene: a student finishes the classes lesson and checks their work. */
export interface DemoCheckResult {
  readonly name: string;
  readonly passed: boolean;
  readonly hint?: string;
}

export const DEMO_STUDENT_LESSON_TITLE = DEMO_AI_DRAFT_2.title;

/** Whale lesson starter. Class exists; spout() body, object, and call are still missing. */
export const DEMO_STUDENT_WHALE_CODE_BASE = `# Call Me Class-mael
# Model a whale from Moby Dick as an object so classes finally click.

class Whale:
    def __init__(self, name, color):
        self.name = name
        self.color = color

    def spout(self):
        # Print a message with this whale's name

`;

/** Text the student "types" to finish spout(), create Moby, and call spout(). */
export const DEMO_STUDENT_WHALE_TYPING = `        print(f"{self.name} the {self.color} whale spouts a fountain!")

moby = Whale("Moby Dick", "white")
moby.spout()`;

export const DEMO_STUDENT_CHECKS: readonly DemoCheckResult[] = [
  { name: "Defines a Whale class", passed: true },
  {
    name: "Creates a Whale object",
    passed: false,
    hint: "Create a Whale with moby = Whale(\"Moby Dick\", \"white\").",
  },
  {
    name: "Calls the spout() method",
    passed: false,
    hint: "Add moby.spout() so your whale actually spouts.",
  },
  {
    name: "Prints the whale's name",
    passed: false,
    hint: "Use self.name inside spout() so the message includes the whale.",
  },
];

/** Simple (single-file) IDE showcase. */
export const DEMO_SIMPLE_CODE = `# Number Guessing Game
import random

secret = random.randint(1, 20)
guesses = 0

print("I'm thinking of a number between 1 and 20.")

while True:
    guess = int(input("Your guess: "))
    guesses += 1
    if guess < secret:
        print("Higher!")
    elif guess > secret:
        print("Lower!")
    else:
        print(f"You got it in {guesses} tries!")
        break
`;

export const DEMO_SIMPLE_CONSOLE: readonly ConsoleLine[] = [
  { stream: "stdout", text: "I'm thinking of a number between 1 and 20." },
  { stream: "stdout", text: "Your guess: 10" },
  { stream: "stdout", text: "Higher!" },
  { stream: "stdout", text: "Your guess: 15" },
  { stream: "stdout", text: "Lower!" },
  { stream: "stdout", text: "Your guess: 13" },
  { stream: "stdout", text: "You got it in 3 tries!" },
];

export const DEMO_SIMPLE_TOUR: readonly DemoTourStep[] = [
  {
    title: "Make a guessing game",
    body: [
      "Your program picks a secret number and lets the player guess.",
      "After each guess it says Higher or Lower until they win.",
    ],
  },
  {
    title: "Loop until correct",
    body: ["A `while True` loop keeps asking until the guess matches the secret."],
    referenceCode: 'while True:\n    guess = int(input("Your guess: "))',
  },
];

/** Advanced (multi-file) IDE showcase. */
export const DEMO_ADVANCED_FILES: readonly { path: string; code: string }[] = [
  {
    path: "main.py",
    code: `from rooms import START_ROOM
from game import play

# Entry point for the text adventure.
play(START_ROOM)
`,
  },
  {
    path: "rooms.py",
    code: `# Each room links to the next by name.
ROOMS = {
    "cave": {"desc": "A damp cave. A torch flickers north.", "north": "hall"},
    "hall": {"desc": "A grand hall full of echoes.", "south": "cave"},
}

START_ROOM = "cave"
`,
  },
  {
    path: "game.py",
    code: `from rooms import ROOMS

def play(room):
    while True:
        print(ROOMS[room]["desc"])
        move = input("Go where? ").strip()
        room = ROOMS[room].get(move, room)
`,
  },
];

export const DEMO_ADVANCED_CONSOLE: readonly ConsoleLine[] = [
  { stream: "stdout", text: "A damp cave. A torch flickers north." },
  { stream: "stdout", text: "Go where? north" },
  { stream: "stdout", text: "A grand hall full of echoes." },
  { stream: "stdout", text: "Go where? south" },
  { stream: "stdout", text: "A damp cave. A torch flickers north." },
];

/** A cohort progress row for the chart showcase. */
export interface DemoProgressRow {
  readonly lessonTitle: string;
  readonly complete: number;
  readonly inProgress: number;
  readonly needsHelp: number;
  readonly notStarted: number;
}

export const DEMO_COHORT_LABEL = "Period 3 · Fall 2026";
export const DEMO_COHORT_TEMPLATE = "Intro to Python";

export const DEMO_PROGRESS_ROWS: readonly DemoProgressRow[] = [
  { lessonTitle: "Variables & Output", complete: 24, inProgress: 3, needsHelp: 1, notStarted: 0 },
  { lessonTitle: "Loops & a Guessing Game", complete: 18, inProgress: 6, needsHelp: 3, notStarted: 1 },
  { lessonTitle: "Lists & Playlists", complete: 12, inProgress: 8, needsHelp: 4, notStarted: 4 },
  { lessonTitle: "Functions: Calculator", complete: 7, inProgress: 9, needsHelp: 5, notStarted: 7 },
  { lessonTitle: "Draw with Turtle", complete: 3, inProgress: 6, needsHelp: 2, notStarted: 17 },
];

export interface DemoProgressTotals {
  readonly complete: number;
  readonly inProgress: number;
  readonly needsHelp: number;
  readonly notStarted: number;
  readonly passRate: number | null;
}

export function demoProgressTotals(rows: readonly DemoProgressRow[]): DemoProgressTotals {
  const totals = rows.reduce(
    (acc, r) => ({
      complete: acc.complete + r.complete,
      inProgress: acc.inProgress + r.inProgress,
      needsHelp: acc.needsHelp + r.needsHelp,
      notStarted: acc.notStarted + r.notStarted,
    }),
    { complete: 0, inProgress: 0, needsHelp: 0, notStarted: 0 }
  );
  const sum = totals.complete + totals.inProgress + totals.needsHelp + totals.notStarted;
  return {
    ...totals,
    passRate: sum > 0 ? Math.round((totals.complete / sum) * 100) : null,
  };
}
